/** Google Identity Services → Firebase credential helper. */

import { GoogleAuthProvider, signInWithCredential, type UserCredential } from 'firebase/auth';
import { auth } from '@/lib/firebase';

const GIS_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: Record<string, unknown>) => void;
          prompt: (listener?: (n: { isNotDisplayed: () => boolean; isSkippedMoment: () => boolean }) => void) => void;
          renderButton: (parent: HTMLElement, config: Record<string, unknown>) => void;
          cancel: () => void;
        };
      };
    };
  }
}

function googleClientId(): string {
  const id = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID as string | undefined;
  if (!id) {
    throw new Error('Google sign-in is not configured (missing VITE_GOOGLE_OAUTH_CLIENT_ID).');
  }
  return id;
}

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GIS_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Failed to load Google Identity Services')));
      return;
    }
    const script = document.createElement('script');
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
    document.head.appendChild(script);
  });
}

/**
 * Opens Google account chooser via GIS and signs into Firebase with the ID token.
 * Avoids Firebase authDomain redirect URI requirements on a new GCP project.
 */
export async function signInWithGoogleAccount(): Promise<UserCredential> {
  await loadGisScript();
  const clientId = googleClientId();

  const idToken = await new Promise<string>((resolve, reject) => {
    let settled = false;
    const finish = (err?: Error, token?: string) => {
      if (settled) return;
      settled = true;
      window.google?.accounts.id.cancel();
      if (container.parentNode) container.parentNode.removeChild(container);
      if (err) reject(err);
      else if (token) resolve(token);
      else reject(new Error('Google sign-in was cancelled.'));
    };

    const container = document.createElement('div');
    container.setAttribute('aria-hidden', 'true');
    container.style.cssText = 'position:fixed;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;';
    document.body.appendChild(container);

    window.google!.accounts.id.initialize({
      client_id: clientId,
      callback: (response: { credential?: string }) => {
        if (response?.credential) finish(undefined, response.credential);
        else finish(new Error('Google did not return a credential.'));
      },
      auto_select: false,
      cancel_on_tap_outside: true,
      context: 'signin',
      use_fedcm_for_prompt: true,
    });

    window.google!.accounts.id.renderButton(container, {
      type: 'standard',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      shape: 'rectangular',
    });

    // Allow GIS to paint the button, then synthesize a user gesture click.
    requestAnimationFrame(() => {
      const clickable =
        container.querySelector<HTMLElement>('div[role="button"]') ||
        container.querySelector('iframe') ||
        (container.firstElementChild as HTMLElement | null);
      if (clickable) {
        clickable.click();
      } else {
        window.google!.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
            finish(
              new Error(
                'Google sign-in UI was blocked. Allow third-party sign-in for this site and try again.',
              ),
            );
          }
        });
      }
    });

    // Safety timeout
    window.setTimeout(() => {
      finish(new Error('Google sign-in timed out. Please try again.'));
    }, 120000);
  });

  const credential = GoogleAuthProvider.credential(idToken);
  return signInWithCredential(auth, credential);
}
